/**
 * Live mode's ear on the client, pure so `bun test` proves it and the phone
 * and the browser read one copy (docs/plans/os-voice-conversation.md, the
 * conversation contract, rules 7, 8 and 20):
 *
 *   earStep        the voice activity detector, block by block, and the
 *                  loud room that raises its threshold
 *   duckDueOf      whether OS's voice is turned down now, and
 *   duckRampOf     the gain ramp that turns it down within 150 ms
 *   duckSettlingOf the first second of a session's first playback, when the
 *                  echo canceller is still learning and nothing ducks
 *   preRollPush    the ring of the newest microphone frames, and
 *   preRollTake    the frames that cover the last stretch asked for
 *   playedMsOf     how much of OS's turn has actually been heard
 *   liveEarOf      one microphone frame through all of it: the `vad`
 *                  frame to send, the duck, and the pre-roll a half-duplex
 *                  gate hands up as it opens
 *
 * The detector is Phase 0's (`scripts/spikes/duck-sim.ts`): 25 ms blocks,
 * speech when two blocks in a row sit 10 dB over the room and above
 * -50 dBFS, over until 300 ms of blocks under it. Levels are the room's
 * share, 0–1 over `EAR_SPAN_DB` above the floor the client learns
 * (`createRoomFloor` on the phone, its port on the web).
 *
 * A loud room is read from the quietest block of each 100 ms, against a
 * quiet room's level (`EAR_QUIET_DB`), not the learnt floor, which follows
 * steady noise up and would hide it: a person talking dips between
 * syllables, a crowd, a TV or a road does not. When those troughs hold over
 * the speech threshold (`loudRoomOf`), the threshold rises above the room,
 * so only a voice over it counts, and OS is ducked but not paused on word
 * count alone (rule 8).
 */
import { duckOf, LOUD_ROOM_SAMPLES, loudRoomOf, type LiveVad } from './live-turn-rules';

/** How long one block is. */
export const EAR_BLOCK_MS = 25;
/** The span of a level, in dB over the room's floor: 0 at the floor, 1 at this far above it. */
export const EAR_SPAN_DB = 30;
/** Speech is a block this far over the room's floor. */
export const VAD_MARGIN_DB = 10;
/** The same, as a level. */
export const VAD_LEVEL = VAD_MARGIN_DB / EAR_SPAN_DB;
/** A quiet room, the reference a loud one is read against. */
export const EAR_QUIET_DB = -60;
/** And never a block quieter than this, whatever the room: digital near-silence is not a voice. */
export const VAD_MIN_DB = -50;
/** How long blocks over the threshold must run before it is speech: two blocks. */
export const VAD_START_MS = 50;
/** How long under the threshold before speech is over: the gap between words is not the end. */
export const VAD_HANG_MS = 300;
/**
 * A voice this far into the span is near the device (18 dB over the room's
 * learnt floor, so relative to the room, not an absolute level). A rough
 * estimate: the server reads it as a weak sign only, and a client that cannot
 * measure it (a simulator on the Mac's mic, a browser mic with automatic
 * gain) sends it as unknown (`nearKnown`).
 */
export const VAD_NEAR_LEVEL = 0.6;
/** The loud room reads one trough per this long. */
export const LOUD_BUCKET_MS = 100;
/** A `vad` frame goes on every change of speaking or near, and with the level at most this often otherwise. */
export const VAD_REPORT_MS = 500;

export type Ear = {
  /** Someone is speaking. */
  readonly speaking: boolean;
  /** When the current speech began, on the ear's own clock (ms of blocks fed); null when nobody speaks. */
  readonly startedAtMs: number | null;
  /** The newest block's level, 0–1. */
  readonly level: number;
  /** The loudest level of the current speech; 0 when nobody speaks. */
  readonly peak: number;
  /** The current speech is near the device. */
  readonly near: boolean;
  /** The room is loud, and the threshold has risen above it. */
  readonly loud: boolean;
  /** A loud room's own threshold, in dBFS; null while the room is not loud. */
  readonly loudDb: number | null;
  /** Ms of blocks fed. */
  readonly clockMs: number;
  /** How long blocks have run over the threshold, and under it. */
  readonly aboveMs: number;
  readonly belowMs: number;
  /** When the current run over the threshold began. */
  readonly runFromMs: number;
  /** Each 100 ms bucket's quietest level, oldest first, at most `LOUD_ROOM_SAMPLES`. */
  readonly troughs: readonly number[];
  /** The bucket being filled: how much of it, and its quietest level so far. */
  readonly bucketMs: number;
  readonly bucketMin: number;
};

export const NEW_EAR: Ear = {
  speaking: false,
  startedAtMs: null,
  level: 0,
  peak: 0,
  near: false,
  loud: false,
  loudDb: null,
  clockMs: 0,
  aboveMs: 0,
  belowMs: 0,
  runFromMs: 0,
  troughs: [],
  bucketMs: 0,
  bucketMin: 1,
};

/** A block's loudness as the room's share, 0–1. */
export const earLevelOf = (db: number, floorDb: number): number => Math.max(0, Math.min(1, (db - floorDb) / EAR_SPAN_DB));

/** One block: its loudness in dBFS, the room's floor under it, and how long it lasts. */
export type EarBlock = { readonly db: number; readonly floorDb: number; readonly ms: number };

/** The ear after one more block. */
export const earStep = (ear: Ear, block: EarBlock): Ear => {
  const level = earLevelOf(block.db, block.floorDb);
  const clockMs = ear.clockMs + block.ms;

  // The loud room, one trough per bucket.
  let troughs = ear.troughs;
  let bucketMs = ear.bucketMs + block.ms;
  let bucketMin = Math.min(ear.bucketMin, earLevelOf(block.db, EAR_QUIET_DB));
  let loud = ear.loud;
  let loudDb = ear.loudDb;
  if (bucketMs >= LOUD_BUCKET_MS) {
    troughs = [...troughs, bucketMin].slice(-LOUD_ROOM_SAMPLES);
    bucketMs = 0;
    bucketMin = 1;
    const room = loudRoomOf(troughs, VAD_LEVEL);
    loud = room.loud;
    loudDb = room.loud ? EAR_QUIET_DB + room.threshold * EAR_SPAN_DB : null;
  }

  const thresholdDb = Math.max(block.floorDb + VAD_MARGIN_DB, loudDb ?? -Infinity, VAD_MIN_DB);
  const above = block.db >= thresholdDb;
  const aboveMs = above ? ear.aboveMs + block.ms : 0;
  const runFromMs = above && ear.aboveMs === 0 ? ear.clockMs : ear.runFromMs;
  const belowMs = above ? 0 : ear.belowMs + block.ms;

  let speaking = ear.speaking;
  let startedAtMs = ear.startedAtMs;
  if (!speaking && aboveMs >= VAD_START_MS) {
    speaking = true;
    startedAtMs = runFromMs;
  } else if (speaking && belowMs >= VAD_HANG_MS) {
    speaking = false;
    startedAtMs = null;
  }
  const peak = speaking ? Math.max(ear.speaking ? ear.peak : 0, level) : 0;
  return {
    speaking,
    startedAtMs,
    level,
    peak,
    near: speaking && peak >= VAD_NEAR_LEVEL,
    loud,
    loudDb,
    clockMs,
    aboveMs,
    belowMs,
    runFromMs,
    troughs,
    bucketMs,
    bucketMin,
  };
};

/* --------------------------------------------------------------- the duck */

/** OS's voice while ducked: a quarter of its volume, about 12 dB down. */
export const DUCK_GAIN = 0.25;
/** The duck lands within this (contract rule 7): three time constants of the ramp. */
export const DUCK_MS = 150;
/** Coming back up is slower, so a breath between words does not pump the voice. */
export const UNDUCK_MS = 450;
/**
 * The first second of a session's first playback: the echo canceller has not
 * learnt the room yet, and ~250 ms of OS leaks at up to -18 dBFS (Phase 0,
 * 0.5 (b)). Nothing ducks on it.
 */
export const DUCK_SETTLE_MS = 1_000;

/**
 * Whether OS's voice is ducked now: only on a full-duplex call (a half-duplex
 * mic does not hear while OS plays), only while it plays, when someone speaks
 * or the room is loud (`duckOf`), and never while the canceller settles.
 */
export const duckDueOf = (input: {
  readonly full: boolean;
  readonly playing: boolean;
  readonly speaking: boolean;
  readonly loud: boolean;
  readonly settling: boolean;
}): boolean => input.full && input.playing && !input.settling && duckOf({ speaking: input.speaking, near: false, level: 0 }, input.loud);

/**
 * The gain ramp for a duck or its release, as `setTargetAtTime` takes it:
 * the target at the person's volume, and a time constant a third of the
 * ramp, so 95% of the way is reached within it.
 */
export const duckRampOf = (ducked: boolean, volume: number): { readonly target: number; readonly timeConstantS: number } => ({
  target: ducked ? volume * DUCK_GAIN : volume,
  timeConstantS: (ducked ? DUCK_MS : UNDUCK_MS) / 3 / 1000,
});

/** Whether the canceller is still settling: within the first second of the session's first playback. */
export const duckSettlingOf = ({ firstPlaybackAt, now }: { readonly firstPlaybackAt: number | null; readonly now: number }): boolean =>
  firstPlaybackAt !== null && now - firstPlaybackAt < DUCK_SETTLE_MS;

/* ----------------------------------------------------------- the pre-roll */

/** The newest microphone frames, oldest first, and how long they hold. */
export type PreRoll = { readonly frames: readonly { readonly pcm: Uint8Array; readonly ms: number }[]; readonly ms: number };

export const NEW_PRE_ROLL: PreRoll = { frames: [], ms: 0 };

/** One frame of `ms` into the ring; the oldest go once it holds more than `maxMs`. */
export const preRollPush = (roll: PreRoll, pcm: Uint8Array, ms: number, maxMs: number): PreRoll => {
  const frames = [...roll.frames, { pcm, ms }];
  let held = roll.ms + ms;
  while (held > maxMs && frames.length > 0) held -= frames.shift()?.ms ?? 0;
  return { frames, ms: held };
};

/** The newest frames that cover the last `lastMs`, oldest first: the start of speech the detector needed two blocks to see. */
export const preRollTake = (roll: PreRoll, lastMs: number): Uint8Array[] => {
  if (lastMs <= 0) return [];
  const out: Uint8Array[] = [];
  let covered = 0;
  for (let at = roll.frames.length - 1; at >= 0 && covered < lastMs; at -= 1) {
    const frame = roll.frames[at];
    if (frame === undefined) break;
    out.unshift(frame.pcm);
    covered += frame.ms;
  }
  return out;
};

/* ------------------------------------------------------------ what played */

/**
 * How much of OS's turn has been heard, in ms: everything scheduled since the
 * turn began, less what still waits ahead of the player's clock (`nextStart`
 * minus `currentTime`). The lead before the first frame is not heard yet.
 */
export const playedMsOf = ({ scheduledMs, aheadMs }: { readonly scheduledMs: number; readonly aheadMs: number }): number =>
  Math.max(0, Math.round(scheduledMs - Math.max(0, aheadMs)));

/* ------------------------------------------------------- frame by frame */

/** The ear across frames: the VAD, what was last sent of it, and where the half-duplex gate stands. */
export type LiveEarModel = {
  readonly ear: Ear;
  /** The last `vad` frame sent, and when; null before any. */
  readonly sent: LiveVad | null;
  readonly sentAt: number;
  /** The last frame went up: false while the half-duplex gate holds frames back. */
  readonly gateOpen: boolean;
  /** The ear's clock when OS's voice last stopped: speech that began before it is the echo's tail. */
  readonly quietAtMs: number;
};

export const NEW_LIVE_EAR: LiveEarModel = { ear: NEW_EAR, sent: null, sentAt: 0, gateOpen: true, quietAtMs: 0 };

/** The most of the pre-roll the gate ever hands up at once. */
export const LIVE_PRE_ROLL_MAX_MS = 2_000;

/**
 * One recorder frame through the ear. `blocks` are its 25 ms blocks with
 * the room's floor under each; `verdict` is the mic gate's.
 * Answers the ear after it, the `vad` frame to send (null for none), whether
 * OS should play ducked, and how much of the pre-roll goes up before this
 * frame (0 for none).
 *
 * Muted, the ear stops: nothing is heard, speech once reported is said to
 * have stopped, and the ear starts again from its clock's zero (OS's last
 * stop with it). A frame held back by the half-duplex gate is still heard,
 * so the gate knows, when it opens, whether the person had begun: speech
 * that began after OS stopped goes up from its start (the pre-roll), speech
 * that began while OS played is its echo's tail and is not the person's
 * until the ear hears a fresh start.
 */
export const liveEarOf = (
  model: LiveEarModel,
  input: {
    readonly blocks: readonly EarBlock[];
    /** The mic gate's verdict on the frame: sent, kept for a reconnect, or held back. */
    readonly verdict: 'send' | 'buffer' | 'drop';
    readonly muted: boolean;
    readonly playing: boolean;
    readonly full: boolean;
    readonly now: number;
    /** The echo canceller is still settling (`duckSettlingOf`): nothing ducks. */
    readonly settling?: boolean;
    /** The socket is away (reconnecting): no `vad` goes up, and what was sent is forgotten, as the server forgets it. */
    readonly offline?: boolean;
    /** The client can tell a near voice from a far one; false sends `near` as null (unknown). Default true. */
    readonly nearKnown?: boolean;
  },
): { readonly model: LiveEarModel; readonly vad: LiveVad | null; readonly duck: boolean; readonly preRollMs: number } => {
  if (input.offline === true) {
    const heard = liveEarOf(model, { ...input, offline: false });
    return { ...heard, model: { ...heard.model, sent: null, sentAt: 0 }, vad: null };
  }
  const stopped = (ear: Ear, gateOpen: boolean, quietAtMs = model.quietAtMs): { readonly model: LiveEarModel; readonly vad: LiveVad | null; readonly duck: boolean; readonly preRollMs: number } => {
    const vad = model.sent?.speaking === true ? { speaking: false, near: input.nearKnown === false ? null : false, level: 0 } : null;
    return { model: { ...model, ear, gateOpen, quietAtMs, ...(vad === null ? {} : { sent: vad, sentAt: input.now }) }, vad, duck: false, preRollMs: 0 };
  };
  // A fresh ear starts its clock at 0, so OS's last stop is moved to that start: no echo's tail outlives a mute.
  if (input.muted) return stopped(NEW_EAR, false, 0);
  const ear = input.blocks.reduce(earStep, model.ear);
  const goesUp = input.verdict !== 'drop';
  // Speech the person began: anything on a full-duplex call; on a half-duplex one, only what began after OS stopped.
  const fresh = ear.speaking && (input.full || (ear.startedAtMs ?? 0) >= model.quietAtMs);
  if (!goesUp) return stopped(ear, false);
  const opening = !model.gateOpen;
  const frameMs = input.blocks.reduce((sum, block) => sum + block.ms, 0);
  const preRollMs = opening && fresh && ear.startedAtMs !== null ? Math.min(LIVE_PRE_ROLL_MAX_MS, Math.max(0, ear.clockMs - frameMs - ear.startedAtMs)) : 0;
  const now: LiveVad = { speaking: fresh, near: input.nearKnown === false ? null : fresh && ear.near, level: Math.round(ear.level * 100) / 100 };
  const changed = model.sent === null ? now.speaking : model.sent.speaking !== now.speaking || model.sent.near !== now.near;
  const due = changed || (now.speaking && input.now - model.sentAt >= VAD_REPORT_MS);
  const duck = duckDueOf({ full: input.full, playing: input.playing, speaking: fresh, loud: ear.loud, settling: input.settling === true });
  return {
    model: { ...model, ear, gateOpen: true, ...(due ? { sent: now, sentAt: input.now } : {}) },
    vad: due ? now : null,
    duck,
    preRollMs,
  };
};
